/* Only legacy article presentation is normalized. Never rewrite URLs, IDs,
   media, forms, review widgets or app content; Shopify remains the HTML owner. */
(() => {
  'use strict';
  const runtime = 'data-lk-article-runtime';
  const mountEvent = 'lk:article:mount';
  if (document.documentElement.hasAttribute(runtime)) {
    document.dispatchEvent(new CustomEvent(mountEvent));
    return;
  }

  const initialized = new WeakSet();
  const protectedSelector = [
    '[data-lk-article-preserve]', '[data-article-preserve]', 'form', 'button',
    'input', 'select', 'textarea', 'iframe', 'video', 'audio', 'svg', 'math',
    'pre', 'code', 'script', 'template', '.jdgm-widget', '.shopify-buy-frame',
    '.shopify-buy__product', '[data-shopify]', '[data-app]'
  ].join(',');
  const textPresentation = [
    'font', 'font-family', 'font-size', 'line-height', 'letter-spacing',
    'color', 'text-align', 'text-indent', 'margin', 'margin-top', 'margin-right',
    'margin-bottom', 'margin-left', 'margin-inline', 'margin-inline-start',
    'margin-inline-end', 'padding', 'padding-top', 'padding-right',
    'padding-bottom', 'padding-left', 'padding-inline', 'padding-inline-start',
    'padding-inline-end', 'list-style', 'list-style-type', 'list-style-position'
  ];
  const wrapperPresentation = [
    ...textPresentation, 'width', 'max-width', 'min-width', 'float', 'display',
    'flex-direction', 'align-items', 'justify-content', 'gap'
  ];
  const legacyClasses = new Set(['content', 'content_title', 'img_container']);
  const normalizeTitle = text => text.normalize('NFKC').toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

  function normalize(root) {
    if (initialized.has(root)) return;
    const prose = root.querySelector('[data-lk-article-prose]');
    if (!prose) return;
    initialized.add(root);

    if (root.dataset.lkArticleNormalize === 'true') {
      prose.querySelectorAll('#main_container, .content, .img_container').forEach(element => {
        if (element.closest(protectedSelector)) return;
        element.dataset.lkArticleFlow = '';
        wrapperPresentation.forEach(property => element.style.removeProperty(property));
        legacyClasses.forEach(name => element.classList.remove(name));
        // Keep the wrapper and its IDs so existing fragment links still resolve.
      });
      prose.querySelectorAll('h1,h2,h3,h4,h5,h6,p,li,ul,ol,blockquote,span').forEach(element => {
        if (element.closest(protectedSelector)) return;
        textPresentation.forEach(property => element.style.removeProperty(property));
        element.removeAttribute('align');
        legacyClasses.forEach(name => element.classList.remove(name));
        if (!element.getAttribute('style')?.trim()) element.removeAttribute('style');
      });
      prose.querySelectorAll('p').forEach(paragraph => {
        if (paragraph.closest(protectedSelector) || paragraph.id || paragraph.querySelector('a,img,picture,video,audio,iframe,svg,br,[id]')) return;
        if (!paragraph.textContent.replace(/\u00a0/g, '').trim()) paragraph.remove();
      });

      const firstContent = Array.from(prose.querySelectorAll('h1,h2,h3,h4,p,ul,ol,figure,img,picture,video,iframe,blockquote,table,form,details,pre'))
        .find(element => element.textContent.trim() || element.matches('img,picture,video,iframe,form,table'));
      const pageTitle = root.querySelector('[data-lk-article-title]')?.textContent || '';
      if (firstContent?.matches('h1,h2,h3,h4') && !firstContent.closest(protectedSelector) &&
          normalizeTitle(firstContent.textContent) === normalizeTitle(pageTitle)) {
        const anchor = document.createElement('span');
        if (firstContent.id) anchor.id = firstContent.id;
        firstContent.querySelectorAll('[id]').forEach(element => {
          const retained = document.createElement('span');
          retained.id = element.id;
          anchor.append(retained);
        });
        anchor.dataset.lkArticleTitleAnchor = '';
        firstContent.replaceWith(anchor); // Remove duplicate text, not anchor targets.
      }
    }

    const lead = Array.from(prose.querySelectorAll('p')).find(paragraph =>
      paragraph.textContent.trim() && !paragraph.closest('li,blockquote,figcaption,table,' + protectedSelector));
    if (lead) lead.dataset.lkArticleLede = '';
    root.dataset.lkArticleReady = '';
  }

  function mount(scope = document) {
    if (scope instanceof Element && scope.matches('[data-lk-article]')) normalize(scope);
    scope.querySelectorAll('[data-lk-article]').forEach(normalize);
  }
  document.documentElement.setAttribute(runtime, '');
  document.addEventListener(mountEvent, () => mount());
  document.addEventListener('shopify:section:load', event => mount(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), {once: true});
  else mount();
})();